import { DestroyRef, Directive, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { BehaviorSubject, catchError, Observable, skipWhile, Subscription, take, throwError } from 'rxjs';

import { BaasI18n, deepClone } from '@lvflanders/bar';
import { resolveValue$ } from '@lvflanders/bar-ng';

import { BaasConfirmOptions, BaasConfirmService } from '../../baas-confirm';
import { _BarInternalLayoutService } from '../../layout/bar-internal-layout.service';
import { BaasToastMessageService } from '../../toast-messages/baas-toast-message-service';
import { BarEditPageConsumer } from '../bar-edit-page-consumer.contract';
import { BarEditPageLoadArgs } from '../bar-edit-page-load-args.model';
import { BarEditPageLoadReason } from '../bar-edit-page-load-reason.enum';
import { BarEditPageSaveArgs } from '../bar-edit-page-save-args.model';
import { BarEditPageSaveOptions } from '../bar-edit-page-save-options.model';
import { BarEditPageSaveReason } from '../bar-edit-page-save-reason.enum';

// NOTE added this to avoid Ivy compilation errors (classes without decorators)
@Directive()
/** @deprecated Use the <bar-edit-page></bar-edit-page> angular component instead.
 * Edit page base class component. */
export abstract class BarEditPageBaseComponent<TModel, TFormControl extends AbstractControl> implements OnInit, OnDestroy, BarEditPageConsumer {
    /** Activated route */
    readonly #route = inject(ActivatedRoute);
    /** Layout service */
    readonly #destroyRef = inject(DestroyRef);
    /** Translate service */
    readonly #translate = inject(BaasI18n);
    /** Confirmation service */
    readonly #confirm = inject(BaasConfirmService);
    /** Internal layout service */
    readonly #internalLayout = inject(_BarInternalLayoutService);
    readonly #toastMessage = inject(BaasToastMessageService);

    /** Model representing the retrieved data. */
    model: TModel;
    /** The original model. */
    originalModel: TModel;
    /** Represents the reactive form */
    form: TFormControl;
    /** Whether the item in the page is deletable in case in edit modus. A delete button will appear. */
    deletable = false;
    /** If an edit page is disabled or readonly and deletable the delete button doesn't show. With this property we force the delete button to appear in all cases. */
    forceDeletable = false;
    /** Whether the item in the page is saveable in case in edit modus. If true (default) the save and undo buttons appear. */
    saveable = true;
    /** Whether the page is in readonly. This means we don't have save/undo/delete buttons. */
    readonly = false;
    /** Whether editable. If not defined it is immediately editable if not readonly.
     * If readonly == true && editable == true then an edit button appears. */
    editable: boolean = null;
    /** Whether to automatically define the save model. It will be filled in the model property of BarEditPageSaveArgs. */
    autoGenerateSaveModel = true;
    /** Whether the undo action must be confirmed */
    confirmUndo = false;
    /** Whether the save action must be confirmed */
    confirmSave = false;
    /** Whether to show an undo message after doing the undo. */
    showUndoMessage = false;
    /** Whether to show a delete message after doing the delete. */
    showDeleteMessage = false;
    /** Whether to show a save message after doing a save. */
    showSaveMessage = true;
    /** Whether to force to be in valid modus even if the form is invalid */
    forceValid = false;
    /** Whether to force that the edit button is visible even if edit disabled in case using the editable flag */
    forceEditActionVisible = false;
    /** Whether the form must be valid in case the form is pristine.
     * The default behavior is false: In case pristine no validation is done. */
    enforceValidOnPristine = false;
    /** Whether the before unload event, in case the user navigates away (href), is disabled. */
    beforeUnloadDisabled = false;
    /** Whether to show an extra warning when the form is invalid and a save occurs. */
    showSaveMessageOnInvalid = true;
    /** Content to show in the save button. */
    saveContent: string;
    /** Content to show in the undo button. */
    undoContent: string;
    /** Content to show in the delete button. */
    deleteContent: string;
    /** Content to show in the edit button. */
    editContent: string;
    confirmDeleteOptions: BaasConfirmOptions;
    confirmUndoOptions: BaasConfirmOptions;
    confirmSaveOptions: BaasConfirmOptions;

    readonly #savePending$ = new BehaviorSubject(false);
    #disabled = false;
    #submitted = false;
    // edit tooltip override (to show tooltip in case disabled)
    #editTooltip: string = null;
    #formRef: ElementRef<HTMLElement>;
    #formElement: NgForm;
    #firstLoad = true;
    // NOTE this flag is used in case we have disabled/readonly modus with editable functionality (edit button)
    #disabledEditing: boolean = null;
    #deleteTooltip: string;
    #saveTooltip: string;
    #undoTooltip: string;
    #saveMessage: string;
    #undoMessage: string;
    #deleteMessage: string;
    #confirmDeleteMessage: string;
    #confirmSaveMessage: string;
    #confirmUndoMessage: string;
    // subscription in case ngSubmit mechanism is used on form (remember this one to unsubscribe from it)
    #ngSubmitSubscription: Subscription;

    // NOTE here we use { static: false } because form can be assigned with *ngIf
    // we use a set property because we need to do register the form submit to invoke the save when the viewchild gets assigned
    /** Represents the edit form in case the consuming code is using template driven forms. */
    @ViewChild('formElement') set formElement(formElement: NgForm) {
        if (formElement != null) {
            this.#formElement = formElement;
            // unsubscribe in case already subscribed to prevent multiple subscriptions
            if (this.#ngSubmitSubscription) this.#ngSubmitSubscription.unsubscribe();
            // here we make sure a submit performs a save and set up the system for submit on enter
            this.#ngSubmitSubscription = this.formElement.ngSubmit.subscribe(() => {
                this.save({
                    reason: BarEditPageSaveReason.Default,
                    // only make readonly in case the form is editable (and property is being used must not be NULL)
                    exitEdit: this.editable === true
                }).subscribe();
            });
        }
    }
    get formElement(): NgForm {
        return this.#formElement;
    }

    // NOTE here we use { static: false } because form can be assigned with *ngIf
    // we also use a set property because we need to do register the hidden submit button in the form
    /** Represents the element ref of the form */
    @ViewChild('formRef') set formRef(formRef: ElementRef<HTMLElement>) {
        if (formRef != null) {
            this.#formRef = formRef;
            // add a hidden button for screenreaders (web toegankelijkheid)
            const btn = document.createElement('button');
            btn.type = 'submit';
            btn.className = 'sr-only';
            btn.tabIndex = -1;
            btn.innerText = this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE');
            btn.setAttribute('aria-hidden', 'true');
            this.formRef.nativeElement.appendChild(btn);
        }
    }
    get formRef(): ElementRef<HTMLElement> {
        return this.#formRef;
    }

    /** Whether the edit page has changed */
    get dirty(): boolean {
        return this.#formWrapper != null ? this.#formWrapper.dirty : false;
    }

    /** Whether the edit page is valid. */
    get valid(): boolean {
        // the page is valid in case the form is valid or if the consumer forces it to be valid (forceValid)
        return this.forceValid || (this.#formWrapper != null ? this.#formWrapper.valid : true);
    }

    /** Whether the edit page has not changed */
    get pristine(): boolean {
        return !this.dirty;
    }

    /** Whether the edit page is not valid. */
    get invalid(): boolean {
        return !this.valid;
    }

    get showValidations(): boolean {
        return this.submitted && this.invalid && !this.readonly;
    }

    /** Whether the user performed a submit directly or indirectly. */
    get submitted(): boolean {
        return this.#submitted;
    }
    set submitted(value: boolean) {
        this.#submitted = value;
        if (this.formRef != null) {
            // avoid developer to remember setting this
            if (value) {
                this.formRef.nativeElement.classList.add('ng-submitted');
            } else {
                this.formRef.nativeElement.classList.remove('ng-submitted');
            }
        }
        this._onSubmitted(value);
    }

    /** Whether the page is in disabled modus.
     * This means we have save/undo buttons but these appear as disabled and the form gets disabled. */
    get disabled(): boolean {
        // disabled is determined if form is disabled and if not using a form we use the flag to determine if disabled
        return this.#formWrapper != null ? this.#formWrapper.disabled : this.#disabled;
    }
    set disabled(value: boolean) {
        this.#disabled = value;
        this.#applyEnabledDisabledLogic();
    }
    /** Gets the tooltip on the delete button. */
    get deleteTooltip(): string {
        return this.#deleteTooltip || this.deleteContent || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.DELETE');
    }
    set deleteTooltip(value: string) {
        this.#deleteTooltip || value;
    }
    /** Tooltip on the save button */
    get saveTooltip(): string {
        return this.#saveTooltip || this.saveContent || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE');
    }
    set saveTooltip(value: string) {
        this.#saveTooltip = value;
    }
    /** Tooltip on the edit button. */
    get editTooltip(): string {
        return this.#editTooltip || this.editContent || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.EDIT');
    }
    set editTooltip(tooltip: string) {
        this.#editTooltip = tooltip;
    }
    /** Tooltip on the undo button. */
    get undoTooltip(): string {
        return this.#undoTooltip || this.undoContent || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.UNDO');
    }
    set undoTooltip(value: string) {
        this.#undoTooltip = value;
    }
    /** Delete success message. */
    get deleteMessage(): string {
        return this.#deleteMessage || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.DELETE');
    }
    set deleteMessage(value: string) {
        this.#deleteMessage = value;
    }
    /** Save success message. */
    get saveMessage(): string {
        return this.#saveMessage || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.SAVE');
    }
    set saveMessage(value: string) {
        this.#saveMessage = value;
    }
    /** Undo performed message */
    get undoMessage(): string {
        return this.#undoMessage || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.UNDO');
    }
    set undoMessage(value: string) {
        this.#undoMessage = value;
    }
    /** @deprecated Use confirmDeleteOptions. Message within delete confirmation. */
    get confirmDeleteMessage(): string {
        return this.#confirmDeleteMessage || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_DELETE');
    }
    set confirmDeleteMessage(value: string) {
        this.#confirmDeleteMessage = value;
    }
    /** Message within undo confirmation. */
    get confirmUndoMessage(): string {
        return this.#confirmUndoMessage || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_UNDO');
    }
    set confirmUndoMessage(value: string) {
        this.#confirmUndoMessage = value;
    }
    /** Message within save confirmation. */
    get confirmSaveMessage(): string {
        return this.#confirmSaveMessage || this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_SAVE');
    }
    set confirmSaveMessage(value: string) {
        this.#confirmSaveMessage = value;
    }
    /** Reference to form control which can be reactive or template-driven. */
    get #formWrapper(): TFormControl {
        let result: TFormControl = null;

        if (this.form != null) {
            // reactive form gets presedence over reactive
            result = this.form;
        } else if (this.formElement != null) {
            // in case of template driven forms we have reactive form component inside the template driven form
            result = <TFormControl>(<any>this.formElement.form);
        }

        return result;
    }

    ngOnInit(): void {
        this.#defineLayout();

        this.#defineBeforeUnload();

        this.onInit();

        this.#route.data.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((data) => {
            if (data.model !== undefined) {
                // by convention we get the model from the activated route through a resolver
                // but it is not mandatory to use resolver
                // in that case the consumer needs to call the load manually
                const load$ = this.load(data.model, true, BarEditPageLoadReason.Load);
                if (load$ !== undefined) {
                    // if load is not a void we subscribe to it as an observable
                    (<Observable<void>>load$).subscribe();
                }
            }
        });
    }

    ngOnDestroy(): void {
        // complete the pending save behavior subject
        this.#savePending$.complete();
        if (this.#ngSubmitSubscription) this.#ngSubmitSubscription.unsubscribe();
        this.#clearLayout();
        this.onDestroy();
    }

    /** Performs a delete and returns an Observable which is false in case the delete was aborted or if delete is not possible.
     * (disabled, readonly, not deletable). */
    delete(): Observable<boolean> {
        return new Observable<boolean>((subscriber) => {
            if ((this.disabled || this.readonly) && !this.forceDeletable) {
                subscriber.next(false);
                subscriber.complete();
            } else {
                const confirmOptions = this.confirmDeleteOptions ? this.confirmDeleteOptions : {};
                if (!confirmOptions.message) confirmOptions.message = this.confirmDeleteMessage;
                if (!confirmOptions.title) confirmOptions.title = this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE');
                this.#confirm.confirm(confirmOptions).subscribe(() => {
                    //  it's up to the consumer to define what happens after a delete in the onDelete:
                    // - redirect to other URL
                    // - Do a route navigation
                    // - Stay on the same page but
                    // (for example deleting last versie in a dossier which would lead to the previous version)
                    const delete$ = this.onDelete();
                    if (delete$ != null) {
                        this.onDelete().subscribe((result) => {
                            const deleted = typeof result === 'boolean' ? <boolean>result : true;
                            if (deleted && this.showDeleteMessage) {
                                this.#toastMessage.addMessage({
                                    text: this.deleteMessage,
                                    type: 'success'
                                });
                            }
                            subscriber.next(deleted);
                            subscriber.complete();
                        });
                    } else {
                        subscriber.next(false);
                        subscriber.complete();
                    }
                });
            }
        });
    }

    // implement BarEditPageConsumer
    requestSave(input: BarEditPageSaveOptions): Observable<boolean> {
        return this.save(input);
    }

    /**
     * Saves everything and returns an Observable which is false in case invalid.
     */
    save(input: BarEditPageSaveOptions = { reason: BarEditPageSaveReason.Custom }): Observable<boolean> {
        return new Observable<boolean>((subscriber) => {
            const completeSave = (value: boolean) => {
                this.#savePending$.next(false);
                subscriber.next(value);
                subscriber.complete();
            };
            // don't do anything as long as it a save is pending (to avoid having onSave called while a save is already ongoing)
            this.#savePending$
                .pipe(
                    skipWhile((pending) => pending),
                    take(1)
                )
                .subscribe(() => {
                    this.#savePending$.next(true);
                    // pre-saving logic
                    const savingResult$ = resolveValue$(this.onSaving());
                    savingResult$.subscribe((savingResult) => {
                        const options = this.#processSaveOptions(input);
                        // in case the consumer returns false we stop proceeding
                        if (typeof savingResult === 'boolean' && savingResult === false) {
                            completeSave(false);
                        } else {
                            // if nothing has changed or if it is  readonly or disabled we just mark it as completed true
                            // enforceValidOnPristine is used to force validation and save even if it is pristine
                            // NOTE that a form which is invalid but nothing has changed in an autosave scenario does not enforce validity
                            // and doesn't cause an onSave action to be invoked
                            const ommitSave =
                                ((this.readonly || this.disabled) && (this.editable == null || this.editable === false)) ||
                                (options.reason == BarEditPageSaveReason.Autosave && this.pristine && (this.valid || !this.enforceValidOnPristine));
                            if (ommitSave) {
                                completeSave(true);
                            }
                            // if it isn't valid we mark it as completed false
                            else if (!this.valid) {
                                // if not dirty we want to mark the form as dirty so that the save button is enabled
                                this.#markDirty();
                                // show an extra message why validation has failed
                                this.#tryEmitSaveMessageOnInvalid(options.reason);

                                this.submitted = true;
                                completeSave(false);
                            } else {
                                this.submitted = true;

                                const performSave = () => {
                                    const args = new BarEditPageSaveArgs<TModel>();
                                    args.reason = options.reason;
                                    args.model = this.autoGenerateSaveModel ? this.getMutedModel(options.data) : null;
                                    args.data = options.data;
                                    // in case we are doing an auto save we do not need to reload the model because we are navigating away
                                    args.preventLoad = options.reason == BarEditPageSaveReason.Autosave;
                                    const save$ = this.onSave(args);
                                    // check if we have something to save
                                    if (save$ != null) {
                                        save$
                                            .pipe(
                                                catchError((err) => {
                                                    completeSave(false);
                                                    return throwError(err);
                                                })
                                            )
                                            .subscribe((model) => {
                                                // by default we have a success, we can only block the save by returning a boolean of false
                                                let success = true;
                                                let loadModel = true;
                                                // if we got a boolean result and the orinal model is not a boolean
                                                // we assume the boolean indicates whether a success occured or a failure
                                                if (typeof model === 'boolean' && typeof this.model !== 'boolean') {
                                                    // if we got a boolean the success is defined by the result we got (boolean)
                                                    success = <boolean>model;
                                                    // a boolean means we didn't really get a model, so we won't assign it
                                                    model = null;
                                                    loadModel = false;
                                                } else if (model == undefined) {
                                                    // here we have a void return so we won't load anything regarding the model
                                                    loadModel = false;
                                                }

                                                if (success) {
                                                    if (this.showSaveMessage && options.suppressSaveMessage !== true) {
                                                        this.#toastMessage.addMessage({
                                                            text: this.saveMessage,
                                                            type: 'success'
                                                        });
                                                    }
                                                    // here we check if we should go into readonly or disabled after saving
                                                    if (options.exitEdit === true) {
                                                        if (this.#disabledEditing) {
                                                            this.disabled = true;
                                                        } else {
                                                            this.readonly = true;
                                                        }
                                                        // reset flag
                                                        this.#disabledEditing = null;
                                                    }

                                                    const complete = () => {
                                                        this.#markPristine();
                                                        completeSave(true);
                                                    };
                                                    // here we check if the consumer wants to have the load called again
                                                    // (maybe user wants to navigate away whereby the load is irrelevant)
                                                    if (args.preventLoad !== true) {
                                                        // do the load again and call the onLoad
                                                        const load$ = this.load(<TModel>model, loadModel, BarEditPageLoadReason.Save);
                                                        if (load$ !== undefined) {
                                                            (<Observable<void>>load$).subscribe(() => {
                                                                complete();
                                                            });
                                                        } else complete();
                                                    } else complete();
                                                } else {
                                                    // we were not successfull meaning we have to show the error message again (if enabled)
                                                    this.#tryEmitSaveMessageOnInvalid(args.reason);
                                                    completeSave(false);
                                                }
                                            });
                                    } else {
                                        // nothing to save
                                        completeSave(true);
                                    }
                                };
                                // check if we need to ask for a confirmation
                                if (this.confirmSave) {
                                    const confirmOptions = this.confirmSaveOptions ? this.confirmSaveOptions : {};
                                    if (!confirmOptions.message) confirmOptions.message = this.confirmSaveMessage;
                                    if (!confirmOptions.title) confirmOptions.title = this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE');
                                    // we are about to save: ask confirmation
                                    this.#confirm.open(confirmOptions).subscribe((result) => {
                                        if (result) {
                                            // do the save in case the user confirms
                                            performSave();
                                        } else {
                                            // the user refutes to do the save after having been asked for a confirmation
                                            completeSave(false);
                                        }
                                    });
                                } else {
                                    // no confirmation needed -> just perform the save
                                    performSave();
                                }
                            }
                        }
                    });
                });
        });
    }

    undo(): Observable<void> {
        return new Observable<void>((subscriber) => {
            const undoLogic = () => {
                this.model = this.#deepClone(this.originalModel);
                this.#doLoad(BarEditPageLoadReason.Undo);
                this.#markPristine();
                if (this.showUndoMessage) {
                    this.#toastMessage.addMessage({
                        text: this.undoMessage,
                        type: 'info'
                    });
                }

                const complete = () => {
                    // complete the global observable
                    subscriber.next();
                    subscriber.complete();
                };
                // call the undo which can return an Observable of void or be of return type void
                const undo$ = this.onUndo();
                if (undo$ != null) {
                    (<Observable<void>>undo$).subscribe(() => {
                        complete();
                    });
                } else {
                    complete();
                }
            };

            if (this.confirmUndo) {
                const confirmOptions = this.confirmUndoOptions ? this.confirmUndoOptions : {};
                if (!confirmOptions.message) confirmOptions.message = this.confirmUndoMessage;
                if (!confirmOptions.title) confirmOptions.title = this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE');
                this.#confirm.confirm(confirmOptions).subscribe(() => {
                    undoLogic();
                });
            } else {
                undoLogic();
            }
        });
    }

    /** First time initialization. */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected onInit(): void {}

    /**
     * Loading (load the data).
     * @param args
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onLoad(args: BarEditPageLoadArgs): void | Observable<void> {}

    /** Ovveride to perform action when clicking the edit button to go into edit modus (pencil) in case using the editable flag. */
    protected onEdit(): void {}

    /** Override to perform action before the save and before validation.
     * Prevent the save from proceeding by returning (observable of) false. */
    protected onSaving(): void | boolean | Observable<void | boolean> {}

    /** Override to perform the save after validation has succeeded.
     * An observable is returned with the model or a boolean for success or no success (in case the model is not a boolean)
     * or a void if nothing has to happen. */
    protected onSave(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        args?: BarEditPageSaveArgs<TModel>
    ): Observable<TModel | boolean | void> {
        return null;
    }

    /** Override to perform the delete.
     * An observable is returned with a boolean for success or no success/abort or a void which means success. */
    protected onDelete(): Observable<boolean | void> {
        return null;
    }

    /** Override to perform undo. */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected onUndo(): Observable<void> | void {}

    /** Override this for destruction. */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected onDestroy(): void {}

    /** Collects a cloned model including all the changes and extra data. */
    protected getMutedModel(data: any): TModel {
        // in case the model is an object we apply cloning logic (advanced)
        // in case the model is null we check if the form element contains an object (FormGroup versus FormControl/FormArray)
        if (typeof this.model === 'object' || (!this.model && typeof this.form.value === 'object')) {
            const clonedModel = <TModel>{};
            // clone original model
            Object.assign(clonedModel, this.model);
            if (this.form != null) {
                // apply the form if it exists
                Object.assign(clonedModel, this.form.value);
            }
            if (data != null) {
                Object.assign(clonedModel, data);
            }
            return clonedModel;
        } else {
            return this.form.value;
        }
    }

    // Internal function
    protected _onSubmitted(submitted: boolean) {}

    protected load(
        // the model being loaded
        model: TModel = undefined,
        // whether to assign the model and to make internal clone for undo purposes
        loadModel = true,
        // the reason why the load is invoked
        reason: BarEditPageLoadReason = BarEditPageLoadReason.Load,
        // whether to call the onload function again on the consumer (which one does not always want)
        callOnLoad = true
    ): void | Observable<void> {
        if (loadModel) {
            this.model = model;
            // keep original model for undo purposes
            this.originalModel = this.#deepClone(this.model);
        }
        return this.#doLoad(reason, callOnLoad);
    }

    #deepClone(model: TModel): TModel {
        // NOTE in case we have the @lvflanders/bar module we use the deepClone method otherwise the begar function
        return model != null ? deepClone(model) : null;
    }

    #doLoad(reason: BarEditPageLoadReason, callOnLoad = true): void | Observable<void> {
        this.submitted = false;
        if (callOnLoad) {
            const args = new BarEditPageLoadArgs();
            args.reason = reason;
            args.firstLoad = this.#firstLoad;
            args.model = this.model;
            this.#firstLoad = false;
            if (callOnLoad) return this.onLoad(args);
        }
    }

    #applyEnabledDisabledLogic(): void {
        if (this.#formWrapper != null) {
            if (this.#disabled) {
                this.#formWrapper.disable();
            } else {
                this.#formWrapper.enable();
            }
        }
    }

    #defineBeforeUnload(): void {
        window.onbeforeunload = (e: BeforeUnloadEvent) => {
            // in case nothing changed or not active just return
            if (this.beforeUnloadDisabled || this.pristine || this.disabled || this.readonly) {
                return;
            }
            e = e || window.event;
            // For IE and Firefox
            if (e) {
                e.returnValue = this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.LEAVE_PAGE');
            }
            // For Safari / Chrome
            return this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.LEAVE_PAGE');
        };
    }

    #clearLayout(): void {
        // clear the internal content actions because these were defined in this base component
        this.#internalLayout.setInternalContentActions([]);
    }

    #defineLayout(): void {
        this.#internalLayout.setInternalContentActions([
            {
                icon: {
                    class: 'fa fa-pencil-alt',
                    iconOnly: () => this.editContent == null,
                    srText: this.editTooltip
                },
                hidden: () => {
                    // visible in case it is editable and in readonly or editable modus
                    // difference between readonly and disabled is just that the form is disabled in case of disabled
                    const visible = this.editable === true && (this.readonly || this.disabled);
                    return !visible;
                },
                title: () => this.editTooltip,
                action: () => {
                    // action depends on what flag we are using
                    if (this.readonly === true) {
                        // we are in readonly modus to go in edit modus
                        this.readonly = false;
                        this.#disabledEditing = false;
                    } else if (this.disabled === true) {
                        // we are in disabled modus to go in edit modus
                        this.disabled = false;
                        this.#disabledEditing = true;
                    }

                    this.onEdit();
                },
                disabled: () => {
                    return this.editable === false;
                },
                content: () => this.editContent
            },
            {
                icon: {
                    class: 'fas fa-save',
                    iconOnly: () => this.saveContent == null,
                    srText: this.saveTooltip
                },
                disabled: () => {
                    return !this.dirty || this.disabled;
                },
                action: () => {
                    if (this.formElement != null) {
                        // emit the submit on the element (default enter key support)
                        this.formElement.ngSubmit.emit();
                    } else {
                        // the user didn't put the form element template variable just immediately save it
                        this.save({
                            reason: BarEditPageSaveReason.Default,
                            // exit edit modus in case the form is editable (and property is being used must not be NULL)
                            exitEdit: this.editable === true
                        }).subscribe();
                    }
                },
                hidden: () => {
                    return this.readonly || !this.saveable || (this.disabled && this.editable === true);
                },
                color: 'primary',
                title: () => this.saveTooltip,
                content: () => this.saveContent
            },
            {
                icon: {
                    class: 'fa fa-trash',
                    iconOnly: () => this.deleteContent == null,
                    srText: this.deleteTooltip
                },
                disabled: () => {
                    return this.disabled && !this.forceDeletable;
                },
                action: () => {
                    this.delete().subscribe();
                },
                hidden: () => {
                    return (this.readonly && !this.forceDeletable) || !this.deletable || (this.disabled && this.editable === true);
                },
                title: () => this.deleteTooltip,
                content: () => this.deleteContent
            },
            {
                icon: {
                    class: 'fas fa-undo',
                    iconOnly: () => this.undoContent == null,
                    srText: this.undoTooltip
                },
                disabled: () => {
                    return !this.dirty || this.disabled;
                },
                action: () => {
                    this.undo().subscribe();
                },
                hidden: () => {
                    return this.readonly || !this.saveable || (this.disabled && this.editable === true);
                },
                title: () => this.undoTooltip,
                content: () => this.undoContent
            }
        ]);
    }

    #markDirty(): void {
        if (this.#formWrapper != null) {
            this.#formWrapper.markAsDirty();
        }
    }

    #markPristine(): void {
        if (this.#formWrapper != null) {
            this.#formWrapper.markAsPristine();
        }
    }

    #processSaveOptions(options: BarEditPageSaveOptions): BarEditPageSaveOptions {
        // make sure we have an options object with default settings
        if (options == null) {
            options = new BarEditPageSaveOptions();
        }
        if (options.reason == null) {
            // if no reason defined we assume it is custom
            options.reason = BarEditPageSaveReason.Custom;
        }

        return options;
    }

    #tryEmitSaveMessageOnInvalid(reason: BarEditPageSaveReason): void {
        if (this.showSaveMessageOnInvalid) {
            this.#toastMessage.addMessage({
                text: this.#translate.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.' + (reason == BarEditPageSaveReason.Autosave ? 'INVALID_AUTOSAVE' : 'INVALID')),
                type: 'error'
            });
        }
    }
}
